import React, { useEffect, useState } from 'react';
import { Modal, List, Button, Breadcrumb, Spin, message, Typography } from 'antd';
import { FolderOpenOutlined, FileTextOutlined, HomeOutlined, DownloadOutlined, ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import Editor from "@monaco-editor/react";

const { Text } = Typography;

interface FileItem {
    name: string;
    type: 'folder' | 'file';
    path: string;
}

interface IdeFileBrowserProps {
    visible: boolean;
    onClose: () => void;
    userId: string | null;
    studentName: string;
}

const IdeFileBrowserModal: React.FC<IdeFileBrowserProps> = ({ visible, onClose, userId, studentName }) => {
    const [currentPath, setCurrentPath] = useState('');
    const [files, setFiles] = useState<FileItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [fileContent, setFileContent] = useState<string | null>(null);
    const [viewingFile, setViewingFile] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (visible && userId) {
            setCurrentPath('');
            fetchFiles('');
            setViewingFile(null);
            setFileContent(null);
        }
    }, [visible, userId]);

    const fetchFiles = async (path: string) => {
        if (!userId) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';
            const url = `${baseUrl}:8061/jutge/ide/admin/files?userId=${userId}&path=${encodeURIComponent(path)}`;

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch files');
            const rawData = await response.json();

            // Map backend isDir to type
            const data: FileItem[] = rawData.map((item: any) => ({
                name: item.name,
                path: item.path,
                type: item.isDir ? 'folder' : 'file'
            }));

            // Sort: folders first, then files
            const sorted = data.sort((a, b) => {
                if (a.type === b.type) return a.name.localeCompare(b.name);
                return a.type === 'folder' ? -1 : 1;
            });
            setFiles(sorted);
        } catch (error) {
            message.error('Error loading files');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchFileContent = async (path: string) => {
        if (!userId) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';
            const url = `${baseUrl}:8061/jutge/ide/admin/file?userId=${userId}&path=${encodeURIComponent(path)}`;

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch content');
            const data = await response.json();
            setFileContent(data.content);
            setViewingFile(path);
        } catch (error) {
            message.error('Error reading file');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!userId || !viewingFile || fileContent === null) return;
        setSaving(true);
        try {
            const token = localStorage.getItem('authToken');
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost';
            const response = await fetch(`${baseUrl}:8061/jutge/ide/admin/file`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    userId: userId,
                    path: viewingFile,
                    content: fileContent
                })
            });

            if (response.ok) {
                message.success('Archivo guardado correctamente');
            } else {
                const err = await response.json();
                message.error('Error al guardar: ' + (err.error || 'Unknown error'));
            }
        } catch (error) {
            message.error('Error de red al guardar');
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const handleItemClick = (item: FileItem) => {
        if (item.type === 'folder') {
            const newPath = item.path;
            setCurrentPath(newPath);
            fetchFiles(newPath);
        } else {
            fetchFileContent(item.path);
        }
    };

    const handleBreadcrumbClick = (path: string) => {
        setCurrentPath(path);
        fetchFiles(path);
        setViewingFile(null);
    };

    const breadcrumbItems = () => {
        const parts = currentPath.split('/').filter(p => p);
        const items = [{
            title: <HomeOutlined onClick={() => handleBreadcrumbClick('')} style={{ cursor: 'pointer' }} />,
        }];

        let pathAccumulator = '';
        parts.forEach((part, index) => {
            pathAccumulator += (index > 0 ? '/' : '') + part;
            const targetPath = pathAccumulator; // capture scope
            items.push({
                title: <span onClick={() => handleBreadcrumbClick(targetPath)} style={{ cursor: 'pointer' }}>{part}</span>
            });
        });
        return items;
    };

    const getLanguage = (filename: string) => {
        if (filename.endsWith('.java')) return 'java';
        if (filename.endsWith('.js')) return 'javascript';
        if (filename.endsWith('.ts') || filename.endsWith('.tsx')) return 'typescript';
        if (filename.endsWith('.html')) return 'html';
        if (filename.endsWith('.css')) return 'css';
        if (filename.endsWith('.json')) return 'json';
        if (filename.endsWith('.md')) return 'markdown';
        return 'plaintext';
    };

    return (
        <Modal
            title={`Archivos de ${studentName}`}
            open={visible}
            onCancel={onClose}
            width={900}
            footer={[
                <Button key="close" onClick={onClose}>Cerrar</Button>
            ]}
        >
            <div style={{ marginBottom: 16 }}>
                <Breadcrumb items={breadcrumbItems()} />
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
            ) : viewingFile ? (
                <div>
                    <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => setViewingFile(null)}>Volver</Button>
                        <Text strong style={{ marginLeft: 10, flex: 1 }}>{viewingFile.split('/').pop()}</Text>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            onClick={handleSave}
                        >
                            Guardar Cambios
                        </Button>
                    </div>
                    <div style={{
                        height: '500px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px'
                    }}>
                        <Editor
                            height="100%"
                            language={getLanguage(viewingFile)}
                            value={fileContent || ''}
                            onChange={(value) => setFileContent(value || '')}
                            theme="vs-light"
                            options={{
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                fontSize: 14
                            }}
                        />
                    </div>
                </div>
            ) : (
                <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                    {files.length === 0 ? <div style={{ textAlign: 'center', color: '#ccc' }}>Carpeta vacía</div> : (
                        <List
                            itemLayout="horizontal"
                            dataSource={files}
                            renderItem={(item) => (
                                <List.Item
                                    onClick={() => handleItemClick(item)}
                                    style={{ cursor: 'pointer', padding: '10px', borderBottom: '1px solid #eee' }}
                                    actions={item.type === 'file' ? [<Button size="small" icon={<DownloadOutlined />} onClick={(e) => {
                                        e.stopPropagation();
                                        // Simple download trick if needed, or just let them view copy
                                        fetchFileContent(item.path);
                                    }} key="download" />] : []}
                                >
                                    <List.Item.Meta
                                        avatar={item.type === 'folder' ? <FolderOpenOutlined style={{ color: '#1890ff', fontSize: '20px' }} /> : <FileTextOutlined style={{ fontSize: '20px' }} />}
                                        title={<Text>{item.name}</Text>}
                                    />
                                </List.Item>
                            )}
                        />
                    )}
                </div>
            )}
        </Modal>
    );
};

export default IdeFileBrowserModal;
